import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TablePagination,
  CircularProgress,
  Button,
  Stack,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Grid,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { Search as SearchIcon, FilterAlt as FilterAltIcon, ExpandMore as ExpandMoreIcon, ArrowDropDown as ArrowDropDownIcon, FileDownload as FileDownloadIcon, AccountBalance as AccountBalanceIcon } from '@mui/icons-material';
import InformesBackButton from '../../components/shared/InformesBackButton';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import api from '../../services/api';
import tesoreriaService from '../../services/tesoreriaService';
import { searchableSelectAutocompleteProps } from '../../components/SearchableSelect';
import ChequeTerceroDetalleDialog from '../../components/Tesoreria/ChequeTerceroDetalleDialog';
import {
  etiquetaEstadoChequeTercero,
  resolverEstadoChequeTercero,
} from '../../utils/chequeTerceroEstado';
import {
  loadTesoreriaFiltros,
  storedNumber,
  storedString,
  TESORERIA_FILTROS_KEYS,
  usePersistTesoreriaFiltros,
} from '../../utils/tesoreriaFiltrosStorage';

interface ChequesTerceroFiltrosStored {
  searchTerm?: string;
  rowsPerPage?: number;
  nroBanco?: string;
  bancoNombre?: string;
  nroSucursal?: string;
  fechaIngresoDesde?: string;
  fechaIngresoHasta?: string;
  fechaSalidaDesde?: string;
  fechaSalidaHasta?: string;
  fechaVencimientoDesde?: string;
  fechaVencimientoHasta?: string;
  importeDesde?: string;
  importeHasta?: string;
  mcp?: string;
  estado?: string;
}

interface ChequeTercero {
  nroCarga: number;
  nroBanco: number;
  nombreBanco: string;
  nroSucursal: number;
  nombreSucursal: string;
  nroCheque: number;
  nroLibrador: string;
  librador: string;
  importe: number;
  fechaIngreso: string;
  nroMovCajaIngre: number | null;
  nroDestino: number | null;
  fechaSalida: string | null;
  nroMovCajaSalida: number | null;
  fechaVencimiento: string;
  concepto: string;
  fechaEmision: string;
  conciliado: number;
  asentado: number;
  mcp: string;
  nroCuenta: string;
  senial?: number;
  comprobanteIngreso?: string;
  comprobanteSalida?: string;
}

interface Banco {
  id: number;
  nombre: string;
  nroBanco?: string | number;
}

interface SucursalBanco {
  nroBanco: number;
  nroSucursal: number;
  sucursal: string;
}

interface McpOption {
  mcp: string;
  descripcion?: string;
}

const ChequesTercero: React.FC = () => {
  const navigate = useNavigate();
  const loadStored = () =>
    loadTesoreriaFiltros<ChequesTerceroFiltrosStored>(TESORERIA_FILTROS_KEYS.chequesTercero);

  const [cheques, setCheques] = useState<ChequeTercero[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(() => storedNumber(loadStored(), 'rowsPerPage', 20));
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState(() => storedString(loadStored(), 'searchTerm', ''));
  const { enqueueSnackbar } = useSnackbar();

  // Filtros
  const [bancos, setBancos] = useState<Banco[]>([]);
  const [sucursales, setSucursales] = useState<SucursalBanco[]>([]);
  const [nroBanco, setNroBanco] = useState(() => storedString(loadStored(), 'nroBanco', ''));
  const [bancoNombre, setBancoNombre] = useState(() => storedString(loadStored(), 'bancoNombre', ''));
  const [nroSucursal, setNroSucursal] = useState(() => storedString(loadStored(), 'nroSucursal', ''));
  const [fechaIngresoDesde, setFechaIngresoDesde] = useState(() =>
    storedString(loadStored(), 'fechaIngresoDesde', '')
  );
  const [fechaIngresoHasta, setFechaIngresoHasta] = useState(() =>
    storedString(loadStored(), 'fechaIngresoHasta', '')
  );
  const [fechaSalidaDesde, setFechaSalidaDesde] = useState(() =>
    storedString(loadStored(), 'fechaSalidaDesde', '')
  );
  const [fechaSalidaHasta, setFechaSalidaHasta] = useState(() =>
    storedString(loadStored(), 'fechaSalidaHasta', '')
  );
  const [fechaVencimientoDesde, setFechaVencimientoDesde] = useState(() =>
    storedString(loadStored(), 'fechaVencimientoDesde', '')
  );
  const [fechaVencimientoHasta, setFechaVencimientoHasta] = useState(() =>
    storedString(loadStored(), 'fechaVencimientoHasta', '')
  );
  const [importeDesde, setImporteDesde] = useState(() => storedString(loadStored(), 'importeDesde', ''));
  const [importeHasta, setImporteHasta] = useState(() => storedString(loadStored(), 'importeHasta', ''));
  const [mcp, setMcp] = useState(() => storedString(loadStored(), 'mcp', ''));
  const [listaMcps, setListaMcps] = useState<McpOption[]>([]);
  const [estado, setEstado] = useState(() => storedString(loadStored(), 'estado', ''));
  
  // Modal de banco
  const [openBancoDialog, setOpenBancoDialog] = useState(false);
  const [bancoSearch, setBancoSearch] = useState('');
  const [bancoSeleccionadoId, setBancoSeleccionadoId] = useState<number | null>(null);
  const [bancoPage, setBancoPage] = useState(1);
  const [bancoLoading, setBancoLoading] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [chequeSeleccionado, setChequeSeleccionado] = useState<ChequeTercero | null>(null);
  const [detalleReloadToken, setDetalleReloadToken] = useState(0);

  usePersistTesoreriaFiltros(
    TESORERIA_FILTROS_KEYS.chequesTercero,
    {
      searchTerm,
      rowsPerPage,
      nroBanco,
      bancoNombre,
      nroSucursal,
      fechaIngresoDesde,
      fechaIngresoHasta,
      fechaSalidaDesde,
      fechaSalidaHasta,
      fechaVencimientoDesde,
      fechaVencimientoHasta,
      importeDesde,
      importeHasta,
      mcp,
      estado,
    },
    [
      searchTerm,
      rowsPerPage,
      nroBanco,
      bancoNombre,
      nroSucursal,
      fechaIngresoDesde,
      fechaIngresoHasta,
      fechaSalidaDesde,
      fechaSalidaHasta,
      fechaVencimientoDesde,
      fechaVencimientoHasta,
      importeDesde,
      importeHasta,
      mcp,
      estado,
    ]
  );

  // Cargar sucursales
  useEffect(() => {
    const loadSucursales = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: token ? `Bearer ${token}` : undefined };
        const sucursalesResponse = await api.get('/api/Tesoreria/sucursales-banco', { headers });
        setSucursales(sucursalesResponse.data || []);
      } catch (error: any) {
        console.error('Error al cargar sucursales:', error);
      }
    };
    loadSucursales();
  }, []);

  // Cargar MCPs para el filtro
  useEffect(() => {
    const loadMcps = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: token ? `Bearer ${token}` : undefined };
        const response = await api.get('/api/Tesoreria/cheques-terceros/mcps', { headers });
        const data = response.data || [];
        setListaMcps(Array.isArray(data) ? data.map((it: any) => ({ mcp: it.mcp ?? it.Mcp ?? '', descripcion: it.descripcion ?? it.Descripcion })) : []);
      } catch (error: any) {
        console.error('Error al cargar MCPs:', error);
        setListaMcps([]);
      }
    };
    loadMcps();
  }, []);

  // Cargar bancos para el modal
  useEffect(() => {
    const loadBancos = async (reset = false) => {
      try {
        setBancoLoading(true);
        const pageToLoad = reset ? 1 : bancoPage;
        const items = await tesoreriaService.getBancosPaged({
          searchTerm: bancoSearch || undefined,
          pageNumber: pageToLoad,
          pageSize: 20
        });
        setBancos(prev => reset ? items : [...prev, ...items]);
        if (reset) setBancoPage(1);
      } catch (error) {
        console.error('Error al cargar bancos:', error);
      } finally {
        setBancoLoading(false);
      }
    };

    if (openBancoDialog) {
      const timer = setTimeout(() => loadBancos(true), bancoSearch ? 500 : 0);
      return () => clearTimeout(timer);
    }
  }, [openBancoDialog, bancoSearch]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const params: any = {
          pageNumber: page + 1,
          pageSize: Math.min(rowsPerPage, 20)
        };

        if (searchTerm.trim()) {
          params.searchTerm = searchTerm.trim();
        }

        if (nroBanco) {
          params.nroBanco = parseInt(nroBanco);
        }

        if (nroSucursal) {
          params.nroSucursal = parseInt(nroSucursal);
        }

        if (fechaIngresoDesde) {
          params.fechaDesde = fechaIngresoDesde;
        }

        if (fechaIngresoHasta) {
          params.fechaHasta = fechaIngresoHasta;
        }

        if (fechaSalidaDesde) {
          params.fechaSalidaDesde = fechaSalidaDesde;
        }

        if (fechaSalidaHasta) {
          params.fechaSalidaHasta = fechaSalidaHasta;
        }

        if (fechaVencimientoDesde) {
          params.fechaVencimientoDesde = fechaVencimientoDesde;
        }

        if (fechaVencimientoHasta) {
          params.fechaVencimientoHasta = fechaVencimientoHasta;
        }

        if (importeDesde && !isNaN(parseFloat(importeDesde))) {
          params.importeDesde = parseFloat(importeDesde);
        }

        if (importeHasta && !isNaN(parseFloat(importeHasta))) {
          params.importeHasta = parseFloat(importeHasta);
        }

        if (mcp.trim()) {
          params.mcp = mcp.trim();
        }

        if (estado.trim()) {
          params.estado = estado.trim();
        }

        const response = await api.get('/api/Tesoreria/cheques-terceros', {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          },
          params
        });

        const data = response.data;
        let chequesArray: ChequeTercero[] = [];
        let count = 0;
        
        if (data?.chequesTerceros && Array.isArray(data.chequesTerceros)) {
          chequesArray = data.chequesTerceros;
          count = data.totalCount || data.chequesTerceros.length;
        } else if (data?.items && Array.isArray(data.items)) {
          chequesArray = data.items;
          count = data.totalCount || data.items.length;
        } else if (Array.isArray(data)) {
          chequesArray = data;
          count = data.length;
        }
        
        setCheques(chequesArray);
        setTotalCount(count);
      } catch (error: any) {
        console.error('Error al obtener los cheques de tercero:', error);
        enqueueSnackbar(
          error.response?.data?.message || 'No se pudieron cargar los cheques de tercero.',
          { variant: 'error' }
        );
        setCheques([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    };

    const timer = setTimeout(() => {
      loadData();
    }, searchTerm.trim() ? 500 : 0);

    return () => clearTimeout(timer);
  }, [searchTerm, page, rowsPerPage, nroBanco, nroSucursal, fechaIngresoDesde, fechaIngresoHasta, fechaSalidaDesde, fechaSalidaHasta, fechaVencimientoDesde, fechaVencimientoHasta, importeDesde, importeHasta, mcp, estado, enqueueSnackbar, detalleReloadToken]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = Math.min(parseInt(event.target.value, 10), 20);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
    setPage(0);
  };

  const handleBancoSelect = () => {
    const banco = bancos.find(b => b.id === bancoSeleccionadoId);
    if (banco && banco.nroBanco) {
      setNroBanco(banco.nroBanco.toString());
      setBancoNombre(`${banco.nombre} (${banco.nroBanco})`);
      setNroSucursal('');
      setPage(0);
    }
    setOpenBancoDialog(false);
    setBancoSearch('');
  };

  const handleSucursalChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNroSucursal(event.target.value);
    setPage(0);
  };

  const handleClearFilters = () => {
    setNroBanco('');
    setBancoNombre('');
    setNroSucursal('');
    setFechaIngresoDesde('');
    setFechaIngresoHasta('');
    setFechaSalidaDesde('');
    setFechaSalidaHasta('');
    setFechaVencimientoDesde('');
    setFechaVencimientoHasta('');
    setImporteDesde('');
    setImporteHasta('');
    setMcp('');
    setEstado('');
    setSearchTerm('');
    setPage(0);
  };

  const handleBancoScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const bottom = e.currentTarget.scrollHeight - e.currentTarget.scrollTop === e.currentTarget.clientHeight;
    if (bottom && !bancoLoading) {
      setBancoPage(prev => prev + 1);
    }
  };

  const sucursalesFiltradas = nroBanco
    ? sucursales.filter(s => s.nroBanco === parseInt(nroBanco))
    : [];

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('es-AR', {
        dateStyle: 'short'
      }).format(date);
    } catch {
      return dateString;
    }
  };

  const getEstadoChip = (cheque: ChequeTercero) => {
    const estado = resolverEstadoChequeTercero(cheque);
    const label = etiquetaEstadoChequeTercero(estado);
    const color =
      estado === 'anulado'
        ? 'default'
        : estado === 'conciliado'
          ? 'success'
          : estado === 'entregado'
            ? 'warning'
            : 'info';
    return <Chip label={label} color={color} size="small" />;
  };

  const getEstadoTexto = (cheque: ChequeTercero) =>
    etiquetaEstadoChequeTercero(resolverEstadoChequeTercero(cheque));

  const buildExportParams = () => {
    const params: any = { pageNumber: 1, pageSize: 1000 };
    if (searchTerm.trim()) params.searchTerm = searchTerm.trim();
    if (nroBanco) params.nroBanco = parseInt(nroBanco);
    if (nroSucursal) params.nroSucursal = parseInt(nroSucursal);
    if (fechaIngresoDesde) params.fechaDesde = fechaIngresoDesde;
    if (fechaIngresoHasta) params.fechaHasta = fechaIngresoHasta;
    if (fechaSalidaDesde) params.fechaSalidaDesde = fechaSalidaDesde;
    if (fechaSalidaHasta) params.fechaSalidaHasta = fechaSalidaHasta;
    if (fechaVencimientoDesde) params.fechaVencimientoDesde = fechaVencimientoDesde;
    if (fechaVencimientoHasta) params.fechaVencimientoHasta = fechaVencimientoHasta;
    if (importeDesde && !isNaN(parseFloat(importeDesde))) params.importeDesde = parseFloat(importeDesde);
    if (importeHasta && !isNaN(parseFloat(importeHasta))) params.importeHasta = parseFloat(importeHasta);
    if (mcp.trim()) params.mcp = mcp.trim();
    if (estado.trim()) params.estado = estado.trim();
    return params;
  };

  const handleExportExcel = async () => {
    try {
      setExportingExcel(true);
      enqueueSnackbar('Obteniendo datos para exportar...', { variant: 'info' });
      const token = localStorage.getItem('token');
      const response = await api.get('/api/Tesoreria/cheques-terceros', {
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
        params: buildExportParams()
      });
      const data = response.data;
      let items: ChequeTercero[] = [];
      if (data?.chequesTerceros && Array.isArray(data.chequesTerceros)) items = data.chequesTerceros;
      else if (data?.items && Array.isArray(data.items)) items = data.items;
      else if (Array.isArray(data)) items = data;
      if (items.length === 0) {
        enqueueSnackbar('No hay datos para exportar', { variant: 'warning' });
        return;
      }
      const datos = items.map(c => ({
        'Nro. Carga': c.nroCarga || '',
        'Nro. Cheque': c.nroCheque || '',
        'Banco': c.nombreBanco?.trim() || '',
        'Sucursal': c.nombreSucursal?.trim() || '',
        'Librador': c.librador?.trim() || c.nroLibrador?.trim() || '',
        'Importe': c.importe || 0,
        'Fecha Emisión': formatDate(c.fechaEmision),
        'Fecha Vencimiento': formatDate(c.fechaVencimiento),
        'Fecha Ingreso': formatDate(c.fechaIngreso),
        'Fecha Salida': formatDate(c.fechaSalida || ''),
        'Comprobante Ingreso': c.comprobanteIngreso?.trim() || '',
        'Comprobante Salida': c.comprobanteSalida?.trim() || '',
        'Concepto': c.concepto?.trim() || '',
        'MCP': c.mcp?.trim() || '',
        'Estado': getEstadoTexto(c)
      }));
      const ws = XLSX.utils.json_to_sheet(datos);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Cheques de Tercero');
      const fileName = `Cheques_Tercero_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      enqueueSnackbar(`Exportado correctamente (${items.length} registros)`, { variant: 'success' });
    } catch (error: any) {
      console.error('Error al exportar:', error);
      enqueueSnackbar(error.response?.data?.message || 'Error al exportar a Excel', { variant: 'error' });
    } finally {
      setExportingExcel(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {/* Header */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <InformesBackButton to="/tesoreria/informes" useBrowserBack />
              <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
                Cheques de Tercero
              </Typography>
            </Stack>
            <Button
              variant="outlined"
              color="success"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportExcel}
              disabled={loading || exportingExcel || totalCount === 0}
            >
              {exportingExcel ? 'Exportando...' : 'Exportar Excel'}
            </Button>
          </Stack>

          {/* Búsqueda */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Buscar por librador, número de cheque..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ maxWidth: 500 }}
            />
          </Box>

          {/* Filtros Avanzados */}
          <Accordion sx={{ mb: 3, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ bgcolor: '#f9f9f9' }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <FilterAltIcon sx={{ color: '#1976d2' }} />
                <Typography variant="h6" sx={{ color: '#1976d2' }}>
                  Filtros Avanzados
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Banco"
                    value={bancoNombre}
                    placeholder="Seleccionar banco..."
                    size="small"
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setOpenBancoDialog(true)}
                            size="small"
                          >
                            <ArrowDropDownIcon />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    onClick={() => setOpenBancoDialog(true)}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    select
                    fullWidth
                    label="Sucursal"
                    value={nroSucursal}
                    onChange={handleSucursalChange}
                    disabled={!nroBanco}
                    size="small"
                  >
                    <MenuItem value="">Todas</MenuItem>
                    {sucursalesFiltradas.map((sucursal) => (
                      <MenuItem key={sucursal.nroSucursal} value={sucursal.nroSucursal.toString()}>
                        {sucursal.sucursal}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    type="date"
                    fullWidth
                    label="Fecha Ingreso Desde"
                    value={fechaIngresoDesde}
                    onChange={(e) => {
                      setFechaIngresoDesde(e.target.value);
                      setPage(0);
                    }}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    type="date"
                    fullWidth
                    label="Fecha Ingreso Hasta"
                    value={fechaIngresoHasta}
                    onChange={(e) => {
                      setFechaIngresoHasta(e.target.value);
                      setPage(0);
                    }}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    type="date"
                    fullWidth
                    label="Fecha Salida Desde"
                    value={fechaSalidaDesde}
                    onChange={(e) => {
                      setFechaSalidaDesde(e.target.value);
                      setPage(0);
                    }}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    type="date"
                    fullWidth
                    label="Fecha Salida Hasta"
                    value={fechaSalidaHasta}
                    onChange={(e) => {
                      setFechaSalidaHasta(e.target.value);
                      setPage(0);
                    }}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    type="date"
                    fullWidth
                    label="Fecha Venc. Desde"
                    value={fechaVencimientoDesde}
                    onChange={(e) => {
                      setFechaVencimientoDesde(e.target.value);
                      setPage(0);
                    }}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    type="date"
                    fullWidth
                    label="Fecha Venc. Hasta"
                    value={fechaVencimientoHasta}
                    onChange={(e) => {
                      setFechaVencimientoHasta(e.target.value);
                      setPage(0);
                    }}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    type="number"
                    fullWidth
                    label="Monto Desde"
                    value={importeDesde}
                    onChange={(e) => {
                      setImporteDesde(e.target.value);
                      setPage(0);
                    }}
                    placeholder="0"
                    inputProps={{ min: 0, step: 0.01 }}
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    type="number"
                    fullWidth
                    label="Monto Hasta"
                    value={importeHasta}
                    onChange={(e) => {
                      setImporteHasta(e.target.value);
                      setPage(0);
                    }}
                    placeholder="0"
                    inputProps={{ min: 0, step: 0.01 }}
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    select
                    fullWidth
                    label="Estado"
                    value={estado}
                    onChange={(e) => {
                      setEstado(e.target.value);
                      setPage(0);
                    }}
                    size="small"
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="en_cartera">En cartera</MenuItem>
                    <MenuItem value="entregado">Entregado</MenuItem>
                    <MenuItem value="conciliado">Conciliado</MenuItem>
                    <MenuItem value="anulado">Anulado</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <Autocomplete
                    {...searchableSelectAutocompleteProps}
                    options={[{ mcp: '', descripcion: 'Todos' }, ...listaMcps]}
                    getOptionLabel={(option) =>
                      option.mcp ? (option.descripcion ? `${option.mcp} - ${option.descripcion}` : option.mcp) : 'Todos'
                    }
                    value={mcp ? (listaMcps.find((o) => o.mcp === mcp) ?? { mcp, descripcion: mcp }) : { mcp: '', descripcion: 'Todos' }}
                    onChange={(_, newValue) => {
                      setMcp(newValue?.mcp ?? '');
                      setPage(0);
                    }}
                    isOptionEqualToValue={(opt, val) => opt.mcp === val.mcp}
                    filterOptions={(options, { inputValue }) => {
                      if (!inputValue?.trim()) return options;
                      const term = inputValue.trim().toLowerCase();
                      return options.filter(
                        (opt) =>
                          (opt.mcp ?? '').toLowerCase().includes(term) ||
                          (opt.descripcion ?? '').toLowerCase().includes(term)
                      );
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        minHeight: 56,
                        fontSize: '1rem',
                        padding: '8px 14px',
                      },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="MCP"
                        placeholder="Buscar..."
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start">
                                <SearchIcon sx={{ color: '#666', fontSize: 22 }} />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props} key={option.mcp || 'todos'} sx={{ py: 1.5 }}>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <AccountBalanceIcon sx={{ color: '#1976d2', fontSize: 28 }} />
                          <Box>
                            <Typography variant="body1" fontWeight={500}>
                              {option.descripcion || option.mcp || 'Todos'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {option.mcp ? `${option.mcp}` : ''}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    onClick={handleClearFilters}
                    size="small"
                  >
                    Limpiar Filtros
                  </Button>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Tabla */}
          <TableContainer component={Paper} sx={{ borderRadius: 2, overflowX: 'auto' }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Nro. Carga</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Nro. Cheque</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Banco</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Sucursal</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Librador</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Importe</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Fecha Emisión</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Fecha Vencimiento</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Fecha Ingreso</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Fecha Salida</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Comprobante Ingreso</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Comprobante Salida</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Concepto</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>MCP</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Estado</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cheques.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={15} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">
                            {searchTerm ? 'No se encontraron cheques con los criterios de búsqueda' : 'No hay cheques de tercero registrados'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      cheques.map((cheque, index) => (
                        <TableRow
                          key={index}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => setChequeSeleccionado(cheque)}
                        >
                          <TableCell>{cheque.nroCarga || '-'}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{cheque.nroCheque || '-'}</TableCell>
                          <TableCell>{cheque.nombreBanco?.trim() || '-'}</TableCell>
                          <TableCell>{cheque.nombreSucursal?.trim() || '-'}</TableCell>
                          <TableCell>{cheque.librador?.trim() || cheque.nroLibrador?.trim() || '-'}</TableCell>
                          <TableCell>{formatCurrency(cheque.importe || 0)}</TableCell>
                          <TableCell>{formatDate(cheque.fechaEmision)}</TableCell>
                          <TableCell>{formatDate(cheque.fechaVencimiento)}</TableCell>
                          <TableCell>{formatDate(cheque.fechaIngreso)}</TableCell>
                          <TableCell>{formatDate(cheque.fechaSalida || '')}</TableCell>
                          <TableCell>{cheque.comprobanteIngreso?.trim() || '-'}</TableCell>
                          <TableCell>{cheque.comprobanteSalida?.trim() || '-'}</TableCell>
                          <TableCell>{cheque.concepto?.trim() || '-'}</TableCell>
                          <TableCell>{cheque.mcp?.trim() || '-'}</TableCell>
                          <TableCell>
                            {getEstadoChip(cheque)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
                <TablePagination
                  component="div"
                  count={totalCount}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 20]}
                  labelRowsPerPage="Filas por página:"
                  labelDisplayedRows={({ from, to, count }) => `${from}-${Math.min(to, count)} de ${count !== -1 ? count : `más de ${to}`}`}
                />
              </>
            )}
          </TableContainer>
        </CardContent>
      </Card>

      <ChequeTerceroDetalleDialog
        open={!!chequeSeleccionado}
        onClose={() => setChequeSeleccionado(null)}
        cheque={chequeSeleccionado}
        onSaved={() => setDetalleReloadToken((t) => t + 1)}
      />

      {/* Modal de selección de Banco */}
      <Dialog
        open={openBancoDialog}
        onClose={() => setOpenBancoDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Seleccionar Banco</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              placeholder="Buscar banco..."
              value={bancoSearch}
              onChange={(e) => setBancoSearch(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Box>
          <Box sx={{ maxHeight: 360, overflowY: 'auto', pr: 1 }} onScroll={handleBancoScroll}>
            <Grid container spacing={1}>
              {bancos.map((banco, idx) => (
                <Grid item xs={12} key={banco.id ?? idx}>
                  <Card
                    variant={bancoSeleccionadoId === banco.id ? 'elevation' : 'outlined'}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setBancoSeleccionadoId(banco.id)}
                  >
                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5 }}>
                      <input
                        type="radio"
                        checked={bancoSeleccionadoId === banco.id}
                        onChange={() => setBancoSeleccionadoId(banco.id)}
                        style={{ marginRight: 8 }}
                      />
                      <Typography sx={{ fontWeight: 600 }}>
                        {`${banco.nombre}${banco.nroBanco ? ` (${banco.nroBanco})` : ''}`}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
              {!bancos.length && !bancoLoading && (
                <Grid item xs={12}>
                  <Typography variant="body2">No se encontraron bancos.</Typography>
                </Grid>
              )}
              {bancoLoading && (
                <Grid item xs={12}>
                  <Typography variant="body2">Cargando bancos...</Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenBancoDialog(false)}>Cancelar</Button>
          <Button
            variant="contained"
            disabled={bancoSeleccionadoId == null}
            onClick={handleBancoSelect}
          >
            Seleccionar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChequesTercero;

